import React from 'react';
import { Drawer as AntDrawer, DrawerProps as AntDrawerProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export type DrawerSize = 'small' | 'medium' | 'large';

export interface DrawerProps extends Omit<AntDrawerProps, 'size' | 'width' | 'height'> {
  className?: string;
  size?: DrawerSize;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  drawer: {
    '& .ant-drawer-content': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
    },
    '& .ant-drawer-header': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      padding: `${theme.spacing[4]} ${theme.spacing[5]}`,
      '& .ant-drawer-title': {
        color: theme.color.semantic.text.primary,
        fontSize: theme.typography.fontSize.lg,
        fontWeight: theme.typography.fontWeight.semibold,
        lineHeight: theme.typography.lineHeight.normal,
      },
    },
    '& .ant-drawer-close': {
      color: theme.color.semantic.border.primary,
      backgroundColor: 'transparent',
      borderRadius: theme.radius.sm,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
        color: theme.color.semantic.text.primary,
      },
    },
    '& .ant-drawer-body': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      color: theme.color.semantic.text.primary,
      padding: theme.spacing[5],
    },
    '& .ant-drawer-footer': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderTop: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      padding: `${theme.spacing[3]} ${theme.spacing[5]}`,
    },
  },
}));

export const Drawer: React.FC<DrawerProps> = ({
  className = '',
  size = 'medium',
  placement = 'right',
  destroyOnClose = true,
  maskClosable = true,
  ...rest
}) => {
  const classes = useStyles();

  const getWidth = () => {
    if (placement === 'top' || placement === 'bottom') {
      return undefined;
    }
    switch (size) {
      case 'small':
        return 378;
      case 'large':
        return 900;
      case 'medium':
      default:
        return 736;
    }
  };

  const getHeight = () => {
    if (placement === 'left' || placement === 'right') {
      return undefined;
    }
    switch (size) {
      case 'small':
        return 378;
      case 'large':
        return 900;
      case 'medium':
      default:
        return 736;
    }
  };

  const combinedClassName = `${classes.drawer} ${className}`.trim();

  return (
    <AntDrawer
      className={combinedClassName}
      placement={placement}
      destroyOnClose={destroyOnClose}
      maskClosable={maskClosable}
      width={getWidth()}
      height={getHeight()}
      {...rest}
    />
  );
};

export default Drawer;
